import { defineComponent as _defineComponent } from 'vue'

import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  ssrRef,
  useAsync,
  useContext,
  useMeta,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useTranslate } from "@/composables";
import { getCollectionsStore } from "@/store/collections";
import { useCreatorShop } from "@/store/creator-shop";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import logger, { logLevel } from "@/utils/logger";

const __default__ = defineComponent({
  name: "CreatorCollection",
  head: {},
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const componentName = "CreatorCollection";
const store = useStore();
const amplitude = useAmplitude();
const { t } = useTranslate();
const context = useContext();

const titleCollectionNameLength = 28;
const descriptionCollectionNameLength = 70;

const localId = ref(context.params.value.id);
const displayName = ref(context.params.value.displayName);

const paginate = ssrRef(false, `${componentName}-paginate`);
const ltkFetchState = ssrRef<FetchState>(
  { pending: false, error: null, timestamp: -1 },
  `${componentName}-ltkFetchState`,
);

onMounted(() => {
  setReferringTab();
  amplitude?.sendEvent("/page/creator-collection", {
    url: window.location.href,
    "publisher-id": profileId.value ?? "",
    "collection-id": localId.value,
  });
});

onBeforeUnmount(() => {
  useCreatorShop().completeShopTabJourney();
});

const collectionName = computed(() => {
  return getCollectionsStore(store).collections[localId.value]?.title ?? "";
});

const collectionDescription = computed(() => {
  return getCollectionsStore(store).collections[localId.value]?.description;
});

const collectionItems = computed(() => {
  return getCollectionsStore(store).feeds[localId.value]?.ids ?? [];
});

const pageTitle = computed(() => {
  return t("{influencerName} Collections on LTK", { influencerName: displayName.value }).value;
});

const profileAvatar = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.profiles[profileId.value ?? ""]?.avatarUrl;
});

const profileId = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.getIdByDisplayName(displayName.value);
});

const pageMeta = computed(() => {
  // strip of collection and punctuation at the end of collection name
  const _collectionName = collectionName.value
    .trim()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]$/, "")
    .replace(/\bcollection\b$/i, "")
    .trim();

  return {
    title: t("{displayName}'s {collectionName} Collection on LTK", {
      displayName: displayName.value,
      collectionName: context.$metaService.truncateString(_collectionName, titleCollectionNameLength),
    }).value,
    customTitle: true,
    description: t("Shop the looks and styles in this {collectionName} Collection from {displayName} on LTK!", {
      displayName: displayName.value,
      collectionName: context.$metaService.truncateString(_collectionName, descriptionCollectionNameLength),
    }).value,
  };
});

const fetch = async () => {
  try {
    const profilesStore = getProfilesStore(store);
    const collectionsStore = getCollectionsStore(store);

    if (!profilesStore.hasProfileBeenFetchedByName(context.params.value.displayName)) {
      await profilesStore.fetchProfileByDisplayName(context.params.value.displayName);
    }

    if (collectionsStore.collections[context.params.value.id] === undefined) {
      await Promise.all([
        collectionsStore.fetchCollectionInfo({ id: context.params.value.id }),
        collectionsStore.fetchCollectionItems({ id: context.params.value.id }),
      ]);
    } else {
      await collectionsStore.fetchCollectionItems({ id: context.params.value.id });
    }
  } catch (err) {
    logger(logLevel.ERROR, "Error fetching collection", {}, err);
    context.error({
      statusCode: handleErrorStatusCode(err),
      message: `Error fetching collection (${context.params.value.id}) - ${formatErrorMessage(err)}`,
    });
  }
  paginate.value = true;
  return context.params.value;
};

const onPaging = async () => {
  const collectionsStore = getCollectionsStore(store);
  if (paginate.value === true || !collectionsStore.hasFeedBeenFetched(localId.value)) {
    try {
      await collectionsStore.fetchCollectionItems({ id: localId.value });
    } catch (error) {
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(error),
          errorCode: "collection-items-not-found",
          developerMessage: `Error fetching collection items (${localId.value})`,
        },
        originalError: error,
      });
    }
  }
  paginate.value = true;
};

const setReferringTab = () => {
  useCreatorShop().setReferringShopTab();
};

useAsync(async () => {
  await fetch();
  return true;
}, `${componentName}-useAsync`);

useMeta(() => {
  const head = context.$metaService.setPageMeta(pageMeta.value);
  if (profileAvatar.value) {
    const height = 400;
    const width = 400;
    const image = `${profileAvatar.value}?fit=crop&w=${width}&h=${height}`;

    head.meta?.push(...context.$metaService.setOGImg(image, width));
  }
  return head;
});

return { __sfc: true,componentName, store, amplitude, t, context, titleCollectionNameLength, descriptionCollectionNameLength, localId, displayName, paginate, ltkFetchState, collectionName, collectionDescription, collectionItems, pageTitle, profileAvatar, profileId, pageMeta, fetch, onPaging, setReferringTab }
}

})